import React, { useMemo, useState } from 'react';
import { ArrowLeft, CreditCard, Mail, MapPin, MessageSquare, Phone, QrCode, Star, Trash2, UserRound } from 'lucide-react';
import { formatEuropeanDate, formatEuropeanDateTime } from '../../utils/dateFormat';
import { adminDetailCardStyle, adminDetailFieldCardStyle } from '../../components/admin/AdminPanelPrimitives';
import { AdminUserSupportActions } from '../../components/admin/AdminUserSupportActions';

const detailCardStyle = { ...adminDetailCardStyle, padding: 16, borderRadius: 20 };
const detailRowCardStyle = { ...adminDetailFieldCardStyle, padding: 12 };

const SectionTitle = ({ icon, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
    {icon}
    <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, margin: 0, color: '#000' }}>{title}</h3>
  </div>
);

const SmallLabel = ({ children }) => (
  <p style={{ fontSize: 12, color: '#777', margin: 0, textTransform: 'uppercase', letterSpacing: 0.5 }}>{children}</p>
);

const EmptyState = ({ text }) => (
  <p style={{ fontSize: 13, color: '#999', margin: 0, textAlign: 'center', padding: '8px 0' }}>{text}</p>
);

const formatMoney = (value) => `${Number(value || 0).toFixed(2)}€`;

export const AdminGuestDetailView = ({ detail, loading, onBack, onRemoveQr, onDeleteReview, onStartConversation, onRefresh }) => {
  const [openConversationId, setOpenConversationId] = useState(null);

  const guest = detail?.guest;
  const levelInfo = detail?.level_info;
  const activeQrCodes = useMemo(() => (detail?.qr_codes || []).filter((code) => code.is_active), [detail?.qr_codes]);

  return (
    <div data-testid="admin-guest-detail-view">
      <button onClick={onBack} data-testid="admin-guest-detail-back-button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--accent-purple-strong)', marginBottom: 12, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
        <ArrowLeft size={16} /> Back to guests
      </button>

      {loading ? <p style={{ textAlign: 'center', color: '#999', padding: 20 }}>Loading guest details...</p> : null}

      {guest ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={detailCardStyle}>
            <SectionTitle icon={<UserRound size={20} color="var(--accent-purple-strong)" />} title={guest.name || guest.username || 'Guest'} />
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={detailRowCardStyle}>
                <SmallLabel>Contact</SmallLabel>
                <p style={{ margin: '4px 0 0', fontSize: 14, fontWeight: 600 }}>{guest.email || '—'}</p>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#666' }}>{[guest.phone_country_code, guest.phone].filter(Boolean).join(' ') || 'No phone provided'}</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={detailRowCardStyle}>
                  <SmallLabel>Customer ID</SmallLabel>
                  <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600 }}>{guest.customer_id || guest.id}</p>
                </div>
                <div style={detailRowCardStyle}>
                  <SmallLabel>Joined</SmallLabel>
                  <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600 }}>{formatEuropeanDateTime(guest.created_at)}</p>
                </div>
              </div>
            </div>
          </div>

          <AdminUserSupportActions
            userId={guest.id}
            userEmail={guest.email}
            isAdmin={guest.role === 'admin' || Boolean(guest.is_admin)}
          />

          <div style={detailCardStyle}>
            <SectionTitle icon={<Star size={20} color="#00B341" />} title="Discount Level" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, textAlign: 'center' }}>
              <div style={detailRowCardStyle}>
                <SmallLabel>Level</SmallLabel>
                <p style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 700 }}>{levelInfo?.level || 1}</p>
              </div>
              <div style={detailRowCardStyle}>
                <SmallLabel>Discount</SmallLabel>
                <p style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 700 }}>{levelInfo?.discount || 8}%</p>
              </div>
              <div style={detailRowCardStyle}>
                <SmallLabel>Total spent</SmallLabel>
                <p style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 700 }}>{formatMoney(levelInfo?.total_spent)}</p>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <SmallLabel>Progress to next level</SmallLabel>
              <div style={{ marginTop: 6, height: 8, background: '#ECECEC', borderRadius: 999 }}>
                <div style={{ width: `${levelInfo?.progress_percent || 0}%`, height: '100%', background: 'var(--accent-purple-strong)', borderRadius: 999 }} />
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#666' }}>{levelInfo?.progress_percent || 0}% · Next threshold {formatMoney(levelInfo?.next_threshold)}</p>
            </div>
          </div>

          <div style={detailCardStyle}>
            <SectionTitle icon={<QrCode size={20} color="#E53935" />} title="Active QR Codes" />
            {activeQrCodes.length === 0 ? <EmptyState text="No active QR code" /> : activeQrCodes.map((code) => (
              <div key={code.id} style={{ ...detailRowCardStyle, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <SmallLabel>Listing</SmallLabel>
                    <p style={{ margin: '4px 0 0', fontSize: 15, fontWeight: 700 }}>{code.listing_name || 'Allure Invite'}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: '#666' }}>Issued: {formatEuropeanDateTime(code.created_at)}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: '#666' }}>Expires: {formatEuropeanDate(code.expires_at)}</p>
                  </div>
                  <button onClick={() => onRemoveQr(code.id)} data-testid={`admin-remove-guest-qr-${code.id}`} style={{ background: '#FFE9E8', color: '#DD0608', border: 'none', borderRadius: 12, cursor: 'pointer', padding: '10px 12px', alignSelf: 'flex-start' }}>
                    Remove QR
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div style={detailCardStyle}>
            <SectionTitle icon={<CreditCard size={20} color="var(--accent-purple-strong)" />} title="Transactions" />
            {detail?.transactions?.length ? detail.transactions.map((txn) => (
              <div key={txn.id} style={{ ...detailRowCardStyle, marginBottom: 10 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{txn.venue_name || 'Venue'} · {txn.status}</p>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>{formatMoney(txn.discounted_amount)} from {formatMoney(txn.original_amount)} · {formatEuropeanDateTime(txn.created_at)}</p>
              </div>
            )) : <EmptyState text="No transactions yet" />}
          </div>

          <div style={detailCardStyle}>
            <SectionTitle icon={<MapPin size={20} color="#00B341" />} title="Places Visited" />
            {detail?.visited_venues?.length ? detail.visited_venues.map((venue) => (
              <div key={venue.id} style={{ ...detailRowCardStyle, display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{venue.name}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>{venue.address || '—'}</p>
                </div>
                <span style={{ fontSize: 12, color: 'var(--accent-purple-strong)', fontWeight: 700 }}>{venue.category || 'Venue'}</span>
              </div>
            )) : <EmptyState text="No visited venues recorded" />}
          </div>

          <div style={detailCardStyle}>
            <SectionTitle icon={<Star size={20} color="#E5A800" />} title="Reviews Left" />
            {detail?.reviews?.length ? detail.reviews.map((review) => (
              <div key={review.id} style={{ ...detailRowCardStyle, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{review.venue_name || review.target_name || 'Venue review'} · {review.rating || '—'}★</p>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>{review.text || review.comment || 'No comment text'}</p>
                  </div>
                  <button onClick={() => onDeleteReview(review.id)} data-testid={`admin-delete-guest-review-${review.id}`} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                    <Trash2 size={16} color="#DD0608" />
                  </button>
                </div>
              </div>
            )) : <EmptyState text="This guest has not left any reviews yet" />}
          </div>

          <div style={detailCardStyle}>
            <SectionTitle icon={<MessageSquare size={20} color="var(--accent-purple-strong)" />} title="Chats" />
            {guest ? (
              <button onClick={() => onStartConversation(guest.id, guest.name || guest.email)} data-testid={`admin-start-guest-chat-${guest.id}`} style={{ width: '100%', border: '1px solid rgba(112, 63, 246, 0.1)', borderRadius: 16, padding: '12px 16px', background: '#FFFFFF', boxShadow: 'var(--premium-shadow-nested)', cursor: 'pointer', fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
                Start admin ↔ guest chat
              </button>
            ) : null}
            {detail?.conversations?.length ? detail.conversations.map((conversation) => {
              const isOpen = openConversationId === conversation.id;
              return (
                <div key={conversation.id} style={{ ...detailRowCardStyle, marginBottom: 10 }}>
                  <button onClick={() => setOpenConversationId(isOpen ? null : conversation.id)} data-testid={`admin-open-guest-chat-${conversation.id}`} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{conversation.other_user?.name || conversation.other_user?.restaurant_name || 'Conversation'}</p>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>{conversation.last_message || 'Open to view full chat'}</p>
                  </button>
                  {isOpen ? (
                    <div style={{ marginTop: 12, maxHeight: 240, overflowY: 'auto', paddingRight: 4 }}>
                      {conversation.messages?.map((message) => (
                        <div key={message.id} style={{ marginBottom: 10, padding: 10, borderRadius: 12, background: message.sender_id === guest.id ? '#FFFFFF' : 'var(--status-info-surface)' }}>
                          <p style={{ margin: 0, fontSize: 11, color: '#777' }}>{message.sender_id === guest.id ? 'Guest' : 'Other side'} · {formatEuropeanDateTime(message.created_at)}</p>
                          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#111' }}>{message.content}</p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            }) : <EmptyState text="No chat history" />}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminGuestDetailView;